import { ReactSession } from "./secure_reactsession";

// Central helper for building the browser/tab title (and meta <title>) used
// across the app. Titles are formatted as:
//
//   "<Page name> | <Platform> | UserQ"
//
// where <Platform> is "Tester" or "Researcher" — this lets Google Analytics
// segment page views by platform. Use this instead of setting
// document.title / <Helmet><title> directly so the format stays consistent.
const APP_NAME = process.env.REACT_APP_NAME || "UserQ";

const ROLE_LABELS = {
	Researcher: "Researcher",
	Tester: "Tester",
	// Workspace collaborators are researcher-team members, not a separate
	// GA platform — treat them as "Researcher" for segmentation purposes.
	Collaborator: "Researcher",
};

function resolveRole() {
	// 1. Prefer the logged-in user's role, when a session exists.
	const user = ReactSession.get("user");

	if (user && ROLE_LABELS[user.role]) {
		return ROLE_LABELS[user.role];
	}

	// 2. Fall back to the current route: pre-login pages (sign in/up,
	// invite links, etc.) have no session yet but are still unambiguously
	// tester- or researcher-facing by URL prefix.
	if (typeof window !== "undefined" && window.location) {
		const path = window.location.pathname.toLowerCase();

		if (/^\/(t|tester)(\/|$)/.test(path)) {
			return "Tester";
		}

		if (/^\/(r|researcher)(\/|$)/.test(path)) {
			return "Researcher";
		}
	}

	// 3. Last resort for role-agnostic pages (e.g. a bare 404 hit with no
	// session and no role-specific path).
	return "Guest";
}

/**
 * Builds a consistently formatted page title: "<pageName> | <Platform> | UserQ".
 *
 * @param {string} pageName - human readable page name, e.g. "Notification settings"
 * @param {"Tester"|"Researcher"} [roleOverride] - use when the page is
 *   unambiguously role-specific but neither the session nor the URL path
 *   reveals it (e.g. a pre-login invite link that isn't under /researcher
 *   or /tester), so auto-detection would otherwise fall back to "Guest".
 * @returns {string}
 */
export function getPageTitle(pageName, roleOverride) {
	const role = (roleOverride && ROLE_LABELS[roleOverride]) || resolveRole();

	return `${pageName} | ${role} | ${APP_NAME}`;
}
